import type { TranscriptWord } from "@bindery/speech/contract";
import {
  codePoints,
  isCapitalized,
  isWordChar,
  type AsrSpan,
  type CompositionToken,
} from "./schema";

/**
 * A provider word with its segment's average log-probability where the
 * provider reports one. Structural, so it accepts `TranscriptWord` with or
 * without the segment field.
 */
export type AsrWord = Pick<TranscriptWord, "text" | "startMs" | "endMs"> & {
  readonly segmentLogprob?: number | null;
};

/** A `[start, end)` range of UTF-16 offsets into the raw text. */
export interface TextSpan {
  readonly start: number;
  readonly end: number;
}

export interface TokenizeOptions {
  readonly newId: () => string;
  readonly dictationId: string;
  /** `Transcript.flagged`. Flagged with no span to place it masks every word. */
  readonly flagged?: boolean;
  readonly flaggedSpans?: readonly TextSpan[];
}

interface Lexeme {
  readonly kind: "word" | "punct";
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

const MAX_PUNCT_LENGTH = 3;
const SENTENCE_END = /[.!?]/;
const FIRST_PERSON = /^I(['’]|$)/;

const isApostrophe = (ch: string | undefined): boolean =>
  ch === "'" || ch === "’";
const isJoiner = (ch: string | undefined): boolean =>
  isApostrophe(ch) || ch === "-";
const isSpace = (ch: string): boolean => ch.trim() === "";

/** The fold both sides of the alignment are compared in. */
export const alignmentKey = (text: string): string =>
  codePoints(text.toLowerCase()).filter(isWordChar).join("");

const chunk = (lexeme: Lexeme): Lexeme[] => {
  const chars = codePoints(lexeme.text);
  const pieces: Lexeme[] = [];
  let offset = lexeme.start;
  for (let i = 0; i < chars.length; i += MAX_PUNCT_LENGTH) {
    const text = chars.slice(i, i + MAX_PUNCT_LENGTH).join("");
    pieces.push({
      kind: "punct",
      text,
      start: offset,
      end: offset + text.length,
    });
    offset += text.length;
  }
  return pieces;
};

function lex(raw: string): Lexeme[] {
  const chars = codePoints(raw);
  const lexemes: Lexeme[] = [];
  let current: { kind: "word" | "punct"; text: string; start: number } | null =
    null;
  let offset = 0;

  const flush = () => {
    if (current === null) return;
    const lexeme = { ...current, end: current.start + current.text.length };
    lexemes.push(...(lexeme.kind === "punct" ? chunk(lexeme) : [lexeme]));
    current = null;
  };

  for (const [i, ch] of chars.entries()) {
    const next = chars[i + 1];
    const prev = chars[i - 1];
    const inWord = current?.kind === "word";
    const continuesWord =
      isWordChar(ch) ||
      (inWord && isJoiner(ch) && next !== undefined && isWordChar(next)) ||
      // "the dogs' bone": a closing possessive apostrophe stays on its word.
      (inWord &&
        isApostrophe(ch) &&
        (prev === "s" || prev === "S") &&
        (next === undefined || !isWordChar(next)));

    if (isSpace(ch)) {
      flush();
    } else if (continuesWord) {
      if (current?.kind !== "word") {
        flush();
        current = { kind: "word", text: "", start: offset };
      }
      current.text += ch;
    } else {
      if (current?.kind !== "punct") {
        flush();
        current = { kind: "punct", text: "", start: offset };
      }
      current.text += ch;
    }
    offset += ch.length;
  }
  flush();
  return lexemes;
}

// --- alignment

export type Pair = readonly [textIndex: number, asrIndex: number];

export function longestCommonSubsequence(
  a: readonly string[],
  b: readonly string[],
): Pair[] {
  const width = b.length + 1;
  const table = new Uint32Array((a.length + 1) * width);
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i * width + j] =
        a[i] === b[j]
          ? (table[(i + 1) * width + j + 1] ?? 0) + 1
          : Math.max(
              table[(i + 1) * width + j] ?? 0,
              table[i * width + j + 1] ?? 0,
            );
    }
  }
  const pairs: Pair[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      pairs.push([i, j]);
      i++;
      j++;
    } else if (
      (table[(i + 1) * width + j] ?? 0) >= (table[i * width + j + 1] ?? 0)
    ) {
      i++;
    } else {
      j++;
    }
  }
  return pairs;
}

type Timing = Omit<AsrSpan, "dictationId">;

const minLogprob = (words: readonly AsrWord[]): number | null => {
  const known = words
    .map((w) => w.segmentLogprob)
    .filter((p): p is number => typeof p === "number");
  return known.length === 0 ? null : Math.min(...known);
};

const spanOf = (words: readonly AsrWord[]): Timing | null => {
  if (words.length === 0) return null;
  return {
    startMs: Math.min(...words.map((w) => w.startMs)),
    endMs: Math.max(...words.map((w) => w.endMs)),
    segmentLogprob: minLogprob(words),
  };
};

/** Timings for a run of text words against the run of ASR words between two anchors. */
function alignGap(
  textKeys: readonly string[],
  asrWords: readonly AsrWord[],
  asrKeys: readonly string[],
): (Timing | null)[] {
  if (asrWords.length === 0) return textKeys.map(() => null);
  if (textKeys.join("") === asrKeys.join("")) {
    const owner = asrKeys.flatMap((key, index) =>
      codePoints(key).map(() => index),
    );
    let cursor = 0;
    return textKeys.map((key) => {
      const length = codePoints(key).length;
      const owners = owner.slice(cursor, cursor + length);
      cursor += length;
      return spanOf(
        owners.flatMap((index) => {
          const word = asrWords[index];
          return word === undefined ? [] : [word];
        }),
      );
    });
  }
  if (textKeys.length === asrWords.length) {
    return asrWords.map((word) => spanOf([word]));
  }
  // Split and merge differ beyond repair ("3" against "three"): each word
  // gets the whole gap, a clip that contains it rather than one that misses.
  const whole = spanOf(asrWords);
  return textKeys.map(() => whole);
}

export function alignTimings(
  textWords: readonly string[],
  asrWords: readonly AsrWord[],
): (Timing | null)[] {
  const spoken = asrWords.filter((w) => alignmentKey(w.text) !== "");
  const textKeys = textWords.map(alignmentKey);
  const asrKeys = spoken.map((w) => alignmentKey(w.text));
  const anchors = longestCommonSubsequence(textKeys, asrKeys);
  const bounded: Pair[] = [
    ...anchors,
    [textKeys.length, asrKeys.length] as const,
  ];
  const timings: (Timing | null)[] = [];
  let textFrom = 0;
  let asrFrom = 0;
  for (const [textIndex, asrIndex] of bounded) {
    timings.push(
      ...alignGap(
        textKeys.slice(textFrom, textIndex),
        spoken.slice(asrFrom, asrIndex),
        asrKeys.slice(asrFrom, asrIndex),
      ),
    );
    const anchor = spoken[asrIndex];
    if (anchor !== undefined) timings.push(spanOf([anchor]));
    textFrom = textIndex + 1;
    asrFrom = asrIndex + 1;
  }
  return timings;
}

// --- tokens

const overlaps = (lexeme: Lexeme, spans: readonly TextSpan[]): boolean =>
  spans.some((s) => s.start < lexeme.end && lexeme.start < s.end);

const looksProper = (text: string, sentenceStart: boolean): boolean =>
  !sentenceStart && !FIRST_PERSON.test(text) && isCapitalized(text);

/**
 * Raw ASR text to composition tokens, with each word's timing from the
 * provider's words where they are given. Flagged words become text-free
 * `unresolved` slots, the child-facing mask.
 */
export function tokenize(
  raw: string,
  words: readonly AsrWord[] | undefined,
  options: TokenizeOptions,
): CompositionToken[] {
  const lexemes = lex(raw);
  const wordLexemes = lexemes.filter((l) => l.kind === "word");
  const timings =
    words === undefined
      ? wordLexemes.map(() => null)
      : alignTimings(
          wordLexemes.map((l) => l.text),
          words,
        );
  const spans = options.flaggedSpans ?? [];
  const isFlagged =
    options.flagged === true && spans.length === 0
      ? () => true
      : (lexeme: Lexeme) => overlaps(lexeme, spans);

  const tokens: CompositionToken[] = [];
  let wordIndex = 0;
  let sentenceStart = true;
  for (const lexeme of lexemes) {
    if (lexeme.kind === "punct") {
      tokens.push({ kind: "punct", id: options.newId(), text: lexeme.text });
      if (SENTENCE_END.test(lexeme.text)) sentenceStart = true;
      continue;
    }
    const timing = timings[wordIndex] ?? null;
    wordIndex++;
    const asr =
      timing === null ? null : { dictationId: options.dictationId, ...timing };
    tokens.push(
      isFlagged(lexeme)
        ? {
            kind: "unresolved",
            id: options.newId(),
            cause: "flagged",
            asr,
            supersedes: [],
          }
        : {
            kind: "word",
            id: options.newId(),
            text: lexeme.text,
            origin: "dictated",
            asr,
            intent: [],
            resolution: "unreviewed",
            properNoun: looksProper(lexeme.text, sentenceStart),
            supersedes: [],
          },
    );
    sentenceStart = false;
  }
  return tokens;
}
